/**
 * Google Tag Manager DataLayer Utilities
 *
 * Provides typed dataLayer pushes for GTM event tracking.
 * All events follow GA4 recommended event naming conventions.
 */

// Declare dataLayer on window
declare global {
  interface Window {
    /** GTM pushes event records; the gtag() shim in googleAdsConversions pushes argument arrays. */
    dataLayer?: unknown[];
  }
}

/**
 * Push an event to the GTM dataLayer
 */
export function pushToDataLayer(event: string, data?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event,
      ...data,
    });
  }
}

// ============================================================================
// Authentication Events
// ============================================================================

export function trackSignUp(method: 'email' | 'google' | 'apple' = 'email') {
  pushToDataLayer('sign_up', {
    method,
  });
}

export function trackLogin(method: 'email' | 'google' | 'apple' = 'email') {
  pushToDataLayer('login', {
    method,
  });
}

// ============================================================================
// Conversion Events
// ============================================================================

export function trackPurchase(params: {
  transaction_id: string;
  value: number;
  currency?: string;
  plan: 'core' | 'premium' | 'upgrade';
}) {
  const planDisplayName = params.plan === 'core' ? 'Core' : params.plan === 'premium' ? 'Premium' : 'Premium Upgrade';
  pushToDataLayer('purchase', {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency || 'USD',
    items: [
      {
        item_id: params.plan,
        item_name: `SettleWise ${planDisplayName}`,
        price: params.value,
        quantity: 1,
        item_category: 'one_time_purchase',
      },
    ],
  });
}

export function trackBeginCheckout(params: {
  value: number;
  currency?: string;
  plan: string;
}) {
  pushToDataLayer('begin_checkout', {
    value: params.value,
    currency: params.currency || 'USD',
    items: [
      {
        item_id: params.plan,
        item_name: `SettleWise ${params.plan}`,
        price: params.value,
        quantity: 1,
      },
    ],
  });
}

// ============================================================================
// Onboarding Funnel Events
// ============================================================================

export function trackOnboardingStarted() {
  pushToDataLayer('onboarding_started');
}

export function trackOnboardingStep(step: number, stepName: string) {
  pushToDataLayer('onboarding_step', {
    step_number: step,
    step_name: stepName,
  });
}

export function trackOnboardingCompleted() {
  pushToDataLayer('onboarding_completed');
}

export function trackOnboardingSkipClicked(params: {
  step_number: number;
  step_name: string;
}) {
  pushToDataLayer('onboarding_skip_clicked', {
    step_number: params.step_number,
    step_name: params.step_name,
  });
}

export function trackOnboardingSkipped(params: {
  step_number: number;
  step_name: string;
  state_detected: boolean;
  state_source: 'user' | 'ip' | 'none';
}) {
  pushToDataLayer('onboarding_skipped', {
    step_number: params.step_number,
    step_name: params.step_name,
    state_detected: params.state_detected,
    state_source: params.state_source,
  });
}

// ============================================================================
// Feature Engagement Events
// ============================================================================

export function trackStrategyGenerated(params?: {
  is_regeneration?: boolean;
  complexity?: string;
}) {
  pushToDataLayer('strategy_generated', {
    is_regeneration: params?.is_regeneration || false,
    complexity: params?.complexity,
  });
}

export function trackDocumentUpload(params: {
  document_type: string;
  file_count: number;
}) {
  pushToDataLayer('document_upload', {
    document_type: params.document_type,
    file_count: params.file_count,
  });
}

export function trackSageChatMessage() {
  pushToDataLayer('sage_chat_message');
}

export function trackCorrespondenceAnalyzed(params?: {
  file_count?: number;
  tactics_detected?: number;
}) {
  pushToDataLayer('correspondence_analyzed', {
    file_count: params?.file_count,
    tactics_detected: params?.tactics_detected,
  });
}

export function trackNetWorthCalculated(params?: {
  total_assets?: number;
  total_debts?: number;
}) {
  pushToDataLayer('net_worth_calculated', {
    // Don't send actual values for privacy, just ranges
    asset_range: params?.total_assets ? getValueRange(params.total_assets) : undefined,
    has_debts: params?.total_debts ? params.total_debts > 0 : undefined,
  });
}

// ============================================================================
// Lead Generation Events
// ============================================================================

export function trackDemoRequested(params?: {
  source?: string;
}) {
  pushToDataLayer('demo_requested', {
    source: params?.source || 'demo_page',
  });
}

export function trackLeadFormSubmit(params: {
  form_name: string;
  form_location: string;
}) {
  pushToDataLayer('generate_lead', {
    form_name: params.form_name,
    form_location: params.form_location,
  });
}

// ============================================================================
// Page/Content Events
// ============================================================================

export function trackPricingPageView() {
  pushToDataLayer('view_pricing');
}

export function trackPlanSelected(plan: string) {
  pushToDataLayer('plan_selected', {
    plan_name: plan,
  });
}

// ============================================================================
// Retargeting Audience Events
// ============================================================================

/**
 * Track when user views their strategy (for retargeting non-payers)
 * Fire this when a free user sees their blurred/locked strategy
 */
export function trackStrategyViewed(params: {
  has_paid: boolean;
  strategy_id?: string;
}) {
  pushToDataLayer('strategy_viewed', {
    has_paid: params.has_paid,
    strategy_id: params.strategy_id,
  });
}

/**
 * Track checkout abandonment (for cart abandoner retargeting)
 * Fire this when user leaves checkout without completing purchase
 */
export function trackCheckoutAbandoned(params: {
  plan: string;
  value: number;
  step?: string;
}) {
  pushToDataLayer('checkout_abandoned', {
    plan: params.plan,
    value: params.value,
    step: params.step,
  });
}

// ============================================================================
// Paywall Events (Authenticated User Upgrade Funnel)
// ============================================================================

export function trackPaywallShown(params: {
  feature: string;
  required_tier: string;
  current_tier: string;
  page_path: string;
}) {
  pushToDataLayer('paywall_shown', {
    feature: params.feature,
    required_tier: params.required_tier,
    current_tier: params.current_tier,
    page_path: params.page_path,
  });
}

export function trackUpgradeClicked(params: {
  feature: string;
  current_tier: string;
  target_tier: string;
  source: 'paywall' | 'sidebar' | 'banner' | 'limit_warning';
}) {
  pushToDataLayer('upgrade_clicked', {
    feature: params.feature,
    current_tier: params.current_tier,
    target_tier: params.target_tier,
    source: params.source,
  });
}

// ============================================================================
// User Properties (for audience building)
// ============================================================================

export function setUserProperties(params: {
  user_id?: string;
  subscription_tier?: string;
  has_attorney?: boolean;
  state?: string;
  days_since_signup?: number;
}) {
  pushToDataLayer('user_properties', {
    user_id: params.user_id,
    subscription_tier: params.subscription_tier,
    has_attorney: params.has_attorney,
    user_state: params.state,
    days_since_signup: params.days_since_signup,
  });
}

// ============================================================================
// Helpers
// ============================================================================

function getValueRange(value: number): string {
  if (value < 50000) return 'under_50k';
  if (value < 100000) return '50k_100k';
  if (value < 250000) return '100k_250k';
  if (value < 500000) return '250k_500k';
  if (value < 1000000) return '500k_1m';
  return 'over_1m';
}

// ============================================================================
// Convenience export
// ============================================================================

export const gtm = {
  // Auth
  trackSignUp,
  trackLogin,
  // Conversions
  trackPurchase,
  trackBeginCheckout,
  // Onboarding
  trackOnboardingStarted,
  trackOnboardingStep,
  trackOnboardingCompleted,
  trackOnboardingSkipClicked,
  trackOnboardingSkipped,
  // Features
  trackStrategyGenerated,
  trackDocumentUpload,
  trackSageChatMessage,
  trackCorrespondenceAnalyzed,
  trackNetWorthCalculated,
  // Leads
  trackDemoRequested,
  trackLeadFormSubmit,
  // Pages
  trackPricingPageView,
  trackPlanSelected,
  // Paywall / Upgrade funnel
  trackPaywallShown,
  trackUpgradeClicked,
  // Retargeting
  trackStrategyViewed,
  trackCheckoutAbandoned,
  // User
  setUserProperties,
  // Raw
  push: pushToDataLayer,
};
